import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  TextField,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Store,
  Add,
  Refresh,
  FileDownload,
  CheckCircle,
  Cancel,
  Edit,
  Close,
  Category,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip } from '../../../components/Common/DataTable';
import { auth } from '../../../config/firebase';

/**
 * Cabeçalhos com o token do utilizador.
 *
 * Este ecrã falava com a API por `fetch` sem token nenhum, e funcionava — porque
 * o router `/v1/admin/services` não tinha autenticação nas suas 17 rotas, ao
 * contrário de todos os outros routers de administração. Qualquer pessoa criava
 * tipos de serviço no catálogo. Fechado o lado do servidor, é preciso passar a
 * enviar o token daqui.
 */
async function cabecalhos(): Promise<Record<string, string>> {
  const base: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    await auth.authStateReady();
    const utilizador = auth.currentUser;
    if (utilizador) base.Authorization = `Bearer ${await utilizador.getIdToken()}`;
  } catch (erro) {
    console.error('[services] não foi possível obter o token:', erro);
  }
  return base;
}

interface ServiceData {
  id: string;
  codigo: string;
  desig: string;
  descr: string;
  estado: string;
  tipoId: string;
  tipo: string;
  tipoCodigo: string;
  categoriaId: number | null;
  categoria: string | null;
  entidades: number;
}

interface ServiceStats {
  total: number;
  ativos: number;
  inativos: number;
  tipos: number;
}

interface ServiceType {
  id: string;
  nome: string;
  codigo: string;
}

interface ServiceCategory {
  id: number;
  nome: string;
  estado: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';

const Services: React.FC = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState<ServiceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);

  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [stats, setStats] = useState<ServiceStats>({
    total: 0,
    ativos: 0,
    inativos: 0,
    tipos: 0,
  });

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceData | null>(null);
  const [formData, setFormData] = useState({
    codigo: '',
    desig: '',
    descr: '',
    estado: 'A',
    tipoId: '',
    categoriaId: '',
  });
  const [saving, setSaving] = useState(false);

  // Toast notifications
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message: string, severity: 'success' | 'error') => {
    setToast({ open: true, message, severity });
  };

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
      });

      const response = await fetch(`${API_URL}/v1/admin/services?${params}`, {
        headers: await cabecalhos(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setServices(data.data || []);
          setTotalCount(data.pagination?.total || 0);
          if (data.stats) {
            setStats(data.stats);
          }
        }
      } else {
        throw new Error('Failed to fetch services');
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      showToast(t('configServices.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, typeFilter, t]);


  const fetchServiceTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/services/types`, {
        headers: await cabecalhos(),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setServiceTypes(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching service types:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/services/categories`, {
        headers: await cabecalhos(),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCategories(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchServices();
    fetchServiceTypes();
    fetchCategories();
  }, [fetchServices]);

  const handleView = (service: ServiceData) => {
    setSelectedService(service);
    setFormData({
      codigo: service.codigo,
      desig: service.desig,
      descr: service.descr || '',
      estado: service.estado,
      tipoId: service.tipoId,
      categoriaId: service.categoriaId ? String(service.categoriaId) : '',
    });
    setEditDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedService(null);
    setFormData({
      codigo: '',
      desig: '',
      descr: '',
      estado: 'A',
      tipoId: '',
      categoriaId: '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedService(null);
  };

  const handleSave = async () => {
    if (!formData.codigo || !formData.desig || !formData.tipoId) {
      showToast(t('configServices.fillRequired'), 'error');
      return;
    }

    setSaving(true);
    try {
      const url = selectedService
        ? `${API_URL}/v1/admin/services/${selectedService.id}`
        : `${API_URL}/v1/admin/services`;

      const method = selectedService ? 'PUT' : 'POST';

      const body = selectedService
        ? {
            desig: formData.desig,
            descr: formData.descr,
            estado: formData.estado,
            tipoId: formData.tipoId,
            categoriaId: formData.categoriaId ? parseInt(formData.categoriaId) : null,
          }
        : {
            codigo: formData.codigo,
            desig: formData.desig,
            descr: formData.descr,
            estado: formData.estado,
            tipoId: formData.tipoId,
            categoriaId: formData.categoriaId ? parseInt(formData.categoriaId) : null,
          };

      const response = await fetch(url, {
        method,
        headers: await cabecalhos(),
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        showToast(
          selectedService ? t('configServices.serviceUpdated') : t('configServices.serviceCreated'),
          'success'
        );
        handleCloseDialog();
        fetchServices();
      } else {
        showToast(data.error || t('configServices.errorSaving'), 'error');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      showToast(t('configServices.errorSaving'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (service: ServiceData) => {
    if (!window.confirm(t('configServices.confirmDeactivate', { name: service.desig }))) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/v1/admin/services/${service.id}`, {
        method: 'DELETE',
        headers: await cabecalhos(),
      });

      const data = await response.json();

      if (data.success) {
        showToast(t('configServices.serviceDeactivated'), 'success');
        fetchServices();
      } else {
        showToast(data.error || t('configServices.errorDeactivating'), 'error');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      showToast(t('configServices.errorDeactivating'), 'error');
    }
  };

  const columns: Column<ServiceData>[] = [
    {
      id: 'codigo',
      label: t('common.code'),
      minWidth: 80,
      format: (value) => (
        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
          {value}
        </Typography>
      ),
    },
    {
      id: 'desig',
      label: t('configServices.designation'),
      minWidth: 200,
    },
    {
      id: 'descr',
      label: t('common.description'),
      minWidth: 200,
      format: (value) => (
        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      id: 'tipo',
      label: t('configServices.serviceType'),
      minWidth: 120,
      format: (value, row) => (
        <Chip
          label={`${row?.tipoCodigo || ''} - ${value || '-'}`}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      id: 'categoria',
      label: t('common.category'),
      minWidth: 120,
      format: (value) => value || '-',
    },
    {
      id: 'entidades',
      label: t('common.entity'),
      minWidth: 90,
      align: 'center',
      format: (value) => (
        <Chip label={value || 0} size="small" />
      ),
    },
    {
      id: 'estado',
      label: t('common.status'),
      minWidth: 100,
      align: 'center',
      format: (value) => (
        <StatusChip
          status={value === 'A' ? t('common.active') : t('common.inactive')}
          colorMap={{
            [t('common.active')]: 'success',
            A: 'success',
            [t('common.inactive')]: 'default',
            I: 'default',
          }}
        />
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('configServices.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('configServices.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<FileDownload />}>
            {t('common.export')}
          </Button>
          <Button variant="outlined" startIcon={<Add />} onClick={handleAdd}>
            {t('configServices.newService')}
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchServices}>
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                  <Store />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('configServices.totalServices')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                  <CheckCircle />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.ativos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('common.active')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.selected', color: 'text.secondary' }}>
                  <Cancel />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inativos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('common.inactive')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.light', color: 'info.main' }}>
                  <Category />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.tipos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('configServices.serviceTypes')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label={t('common.search')}
              placeholder={t('configServices.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="A">{t('common.active')}</MenuItem>
              <MenuItem value="I">{t('common.inactive')}</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('configServices.serviceType')}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {serviceTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.codigo} - {type.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={services}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          setPage(0);
        }}
        onRefresh={fetchServices}
        onView={handleView}
        onEdit={handleView}
        onDelete={handleDelete}
        rowKey="id"
        emptyMessage={t('configServices.noServices')}
      />

      {/* Edit/Add Service Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedService ? t('configServices.editService') : t('configServices.newService')}
          </Typography>
          <IconButton onClick={handleCloseDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 5 }}
                required
                disabled={!!selectedService}
                helperText={selectedService ? t('configServices.codeNotEditable') : t('configServices.maxChars', { max: 5 })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label={t('configServices.designation')}
                value={formData.desig}
                onChange={(e) => setFormData({ ...formData, desig: e.target.value })}
                inputProps={{ maxLength: 45 }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.description')}
                value={formData.descr}
                onChange={(e) => setFormData({ ...formData, descr: e.target.value })}
                inputProps={{ maxLength: 150 }}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label={t('configServices.serviceType')}
                value={formData.tipoId}
                onChange={(e) => setFormData({ ...formData, tipoId: e.target.value })}
                required
              >
                <MenuItem value="">{t('configServices.selectPlaceholder')}</MenuItem>
                {serviceTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.codigo} - {type.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.category')}
                value={formData.categoriaId}
                onChange={(e) => setFormData({ ...formData, categoriaId: e.target.value })}
              >
                <MenuItem value="">{t('configServices.noneFemale')}</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">{t('common.active')}</MenuItem>
                <MenuItem value="I">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<Edit />}
            disabled={saving}
          >
            {saving ? t('common.saving') : selectedService ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Services;
